import { useCallback, useState } from "react";
import axios from "axios";

import type {
  OfferProduct,
  Price,
  PurchaseType,
  UpsellPageDTO,
  UpsellSubmitProduct,
} from "../types/upsell.types";

export const buildRedirectUrl = (url: string, orderId: string) => {
  if (!url) return "";

  const separator = url.includes("?") ? "&" : "?";
  return orderId ? `${url}${separator}orderId=${orderId}` : url;
};

const emptyPrice: Price = {
  value: null,
  formatted: null,
};

const roundCurrencyValue = (value: number) => Math.round(value * 100) / 100;

const getProductTotalPrice = (product: UpsellSubmitProduct) =>
  product.price?.value ?? 0;

const getProductUnitPrice = (product: UpsellSubmitProduct) => {
  const totalPrice = getProductTotalPrice(product);
  const quantity = product.amount ?? 1;

  if (!quantity || quantity <= 1) {
    return totalPrice;
  }

  // Flex ACF currently stores total package price, while the upsell endpoint
  // expects unit price together with quantity.
  return Number((totalPrice / quantity).toFixed(4));
};

export const normalizeUpsellProduct = (
  product: OfferProduct,
  purchaseType: PurchaseType,
  subscriptionEnabled = product.has_subscription,
  overrides?: Partial<UpsellSubmitProduct>,
): UpsellSubmitProduct => {
  const useSubscription = purchaseType === "subscribe" && subscriptionEnabled;
  const thankYouPageProductName = useSubscription
    ? window.subscription_template?.tr_thankyou_page_product_name
    : undefined;

  return {
    product_name: useSubscription
      ? (product.subscr_product_name ?? "")
      : product.product_name,
    generic_name: useSubscription
      ? (product.subscr_product_name ?? "")
      : product.product_name,
    konnektive_id: useSubscription
      ? (product.subscr_konn_id ?? "")
      : product.konnektive_id,
    sku: useSubscription
      ? (product.subscr_sku ?? product.sku ?? "")
      : product.sku,
    price: useSubscription
      ? (product.subscr_price ?? emptyPrice)
      : product.price,
    old_price: useSubscription
      ? (product.subscr_old_price ?? emptyPrice)
      : product.old_price,
    shipping_price: useSubscription
      ? (product.subscr_shipping_price ?? emptyPrice)
      : emptyPrice,
    shipping_konnektive_id: useSubscription
      ? (product.subscr_shipping_konn_id ?? "")
      : "",
    amount: useSubscription
      ? (product.subscr_duration ?? 1)
      : (product.amount ?? 1),
    is_primary: useSubscription
      ? (product.subscr_is_primary ?? product.is_primary)
      : product.is_primary,
    is_subscription: useSubscription,
    thankYouPageProductName,
    ...overrides,
  };
};

const buildRequestData = (
  products: UpsellSubmitProduct[],
  primaryProduct: UpsellSubmitProduct,
) => {
  const primaryPrice = getProductUnitPrice(primaryProduct);
  const primaryTotalPrice = getProductTotalPrice(primaryProduct);
  const primaryShippingPrice = primaryProduct.shipping_price?.value ?? 0;
  const isSubscription = Boolean(primaryProduct.is_subscription);

  return {
    orderId: window.orderId || "",
    postId: window.postId || null,
    upsell: 1,
    sku: primaryProduct.sku,
    konnektive_id: primaryProduct.konnektive_id,
    upsellType: "upsell",
    bundle_products: products.map((product) => ({
      bp_konnektive_id: product.konnektive_id,
      bp_sku: product.sku,
      bp_price: getProductUnitPrice(product),
      bp_amount: product.amount ?? 1,
      bp_old_price: product.old_price?.value ?? 0,
      primary_product: product.is_primary,
    })),
    price: primaryPrice,
    productOldPrice: primaryProduct.old_price?.value ?? 0,
    productShipPrice: primaryShippingPrice,
    shipPrice: primaryShippingPrice,
    shipping_konnektive_id: primaryProduct.shipping_konnektive_id,
    quantity: primaryProduct.amount ?? 1,
    totalAmount: roundCurrencyValue(primaryTotalPrice),
    variants: null,
    title: primaryProduct.product_name,
    generic_name: primaryProduct.generic_name,
    thankYouPageProductName: primaryProduct.thankYouPageProductName,
    isSubscribe: isSubscription,
    subscription: {
      subscription_active: isSubscription,
    },
    checkoutWarrantyState: false,
    hasWarranty: false,
    warrantyState: false,
  };
};

export const useUpsellSubmit = (data: UpsellPageDTO) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectTo = useCallback((url: string) => {
    const redirectUrl = buildRedirectUrl(url, window.orderId || "");
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, []);

  const submitProducts = useCallback(
    async (
      products: UpsellSubmitProduct[],
      primaryProduct?: UpsellSubmitProduct,
    ) => {
      if (isSubmitting || products.length === 0) return;

      const resolvedPrimary =
        primaryProduct ??
        products.find((product) => product.is_primary) ??
        products[0];

      if (!resolvedPrimary) return;

      setIsSubmitting(true);

      try {
        const response = await axios.post(
          "/wp-json/checkout/v1/order/upsell",
          buildRequestData(products, resolvedPrimary),
        );

        if (response.data?.result === "SUCCESS") {
          redirectTo(data.upsell_links.link_success);
          return;
        }

        redirectTo(data.upsell_links.link_failure);
      } catch (error) {
        console.error("Upsell API error:", error);
        redirectTo(data.upsell_links.link_failure);
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, isSubmitting, redirectTo],
  );

  return {
    isSubmitting,
    submitProducts,
  };
};
